import { flow, getEnv, Instance, types } from "mobx-state-tree";
import { activityPricesStorage, ActivityTypes, getActivityLabel } from "../shop/ActivityPricesStorage";
import { IServiceStorageEnv, ServiceStorage } from "./ServiceStorage";
import { ShopHistoryStorageModel } from "../shop/ShopHistoryStorage";
import { ConversionServiceModel } from "../conversion/VideoConversionService";
import { SegmentationServiceModel } from "../segmentation/SegmentationService";
import { AnalysisServiceModel } from "../analysis/AnalysisService";
import { OcrServiceModel } from "../ocr/OcrService";
import { FileUploadTracker } from "../upload/FileUploadTracker";
import { endpoints } from "../api/ApiEndpoints";

const ServicesModel = types.model({
    videoConversion: types.optional(types.maybe(ConversionServiceModel), undefined),
    videoSegmentation: types.optional(types.maybe(SegmentationServiceModel), undefined),
    videoAnalysis: types.optional(types.maybe(AnalysisServiceModel), undefined),
    ocrService: types.optional(types.maybe(OcrServiceModel), undefined),
})

export const AppStorageModel = types.model({
    step: types.optional(types.number, 1),
    collapsed: types.optional(types.boolean, false),
    services: ServicesModel,
    redoSelection: types.optional(types.frozen<ActivityTypes[]>(), []),
    fileId: types.optional(types.frozen<number | undefined>(), undefined),
    fileGuid: types.optional(types.frozen<string | undefined>(), undefined),
    route: types.string,
    activityType: types.frozen<ActivityTypes[]>(),
    image: types.string,
    titleKey: types.string,
    descriptionkey: types.string,
    navigate: types.optional(types.string, ""),
    errorMessage: types.optional(types.frozen<string>(), "")
}).views((self) => ({
    get canRun() {
        return getEnv<IServiceStorageEnv>(self).uploadFilesService.filesForUpload.length !== 0;
    },
    get fileRoute() {
        return self.route + "?guid=" + self.fileGuid;
    },
    get historyStorage() {
        var api = getEnv<IServiceStorageEnv>(self).api;
        return ShopHistoryStorageModel.create({serviceFilter: self.activityType, clickPath: self.route + "?guid=:guid"}, {api});
    },
    get serviceList(): ServiceStorage[] {
        return Object.values(self.services).filter((s: ServiceStorage) => s !== undefined);
    },
    get isLoading() {
        return this.serviceList.some(service => service === undefined ? false : service.isLoading);
    },
    get redoableServices(): {label: string, service: ActivityTypes}[] {
        return this.serviceList.map((service: ServiceStorage) => {
            return {label: getActivityLabel([service.activityType]), service: service.activityType}
        })
    },
    get redoPrice() {
        return activityPricesStorage.getPrices(self.redoSelection);
    }
})).actions((self) => ({
    setError(message: string) {
        self.errorMessage = message;
    },
}))
.actions((self) => ({
    clear: () => {
        self.fileId = undefined;
        self.serviceList.forEach((element: ServiceStorage) => {
            element.clear();
        });
    },
    setStep(step: number) {
        self.step = step;
    },
    toggleCollapsed(collapsed?: boolean) {
        self.collapsed = collapsed || !self.collapsed;
    },
    getFileId: flow(function* (guid: string) {
        const endpoint = endpoints.get("file-id");
        endpoint.routeParams = [guid!];
        return yield getEnv<IServiceStorageEnv>(self).api.get(endpoint);

    }),
    getFileGuid: flow(function* (id: number) {
        const endpoint = endpoints.get("file-guid");
        endpoint.routeParams = [id!];
        return yield getEnv<IServiceStorageEnv>(self).api.get(endpoint);
    }),
    setFileId(fileId: number) {
        self.fileId = fileId;
        self.serviceList.forEach((element: ServiceStorage) => {
            element.setFileId(fileId);
        });
    },
    setFileGuid(fileGuid: string) {
        self.fileGuid = fileGuid;
        self.serviceList.forEach((element: ServiceStorage) => {
            element.setFileGuid(fileGuid);
        });
    },
    toggleRedoSelection(service: ActivityTypes) {
        if (!self.redoSelection.includes(service)) {
            var temp = [...self.redoSelection];
            temp.push(service);
            self.redoSelection = temp;
        } else {
            var temp = [...self.redoSelection];
            self.redoSelection = temp.filter((a: ActivityTypes) => a !== service)
        }
    },
    load: flow(function* () {
        if (self.fileId === undefined) return;
        for (var service of self.serviceList) {
            try {
                yield service.load();
                self.setError("")
            } catch (e: any) {
                console.log(e)
                self.setError("general.apps.error")
            }
        }
    }),
    startService: flow(function* () {
        if (self.fileId === undefined) return;
        for (var service of self.serviceList) {
            yield service.startService();
        }
    }),
    runAfterUpload: flow(function* () {
        for (var service of self.serviceList) {
            yield service.runAfterUpload();
        }
    }),
    setNavigate(path: string) {
        self.navigate = path;
    },
    resetNavigate() {
        self.navigate = "";
    }
})).actions((self) => ({
    redo: flow(function* () {
        for (var service of self.serviceList) {
            if (self.redoSelection.includes(service.activityType)) yield service.redo();
        }
    }),
    new: flow(function* () {
        self.fileId = undefined;
        for (var service of self.serviceList) {
            yield service.new();
        }
        getEnv<IServiceStorageEnv>(self).uploadFilesService.clear();
        self.setNavigate(self.route);
    }),
    onUploadFinished: flow(function* (file: FileUploadTracker) {
        file.completed();
        self.setFileId(file.id!);
        self.setFileGuid(yield self.getFileGuid(file.id!));
        yield self.runAfterUpload();
        yield self.load();
        yield getEnv<IServiceStorageEnv>(self).creditBalanceStorage.load();
        self.setNavigate(self.fileRoute)
    }),
    onConversionFinished: flow(function*(id: number) {
        if (id == self.fileId) yield self.load();
    }),
})).actions((self) => ({
    upload() {
        getEnv<IServiceStorageEnv>(self).uploadFilesService.startStandaloneUpload(self.onUploadFinished);
    }
})).actions((self) => {
    return {
        afterCreate() {
            self.load();
        }
    }
})

export type AppStorage = Instance<typeof AppStorageModel>;